export function parseChallengeISODate(iso) {
	if (!iso) return null;
	const parsed = new Date(`${iso}T12:00:00`);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function toChallengeISODate(date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

/** Server's challenge calendar date (app time zone); falls back to the browser's local date. */
export function todayChallengeISODate(dayTracking) {
	return dayTracking?.today || toChallengeISODate(new Date());
}

export function challengeCalendarDays(startsOn, endsOn) {
	const start = parseChallengeISODate(startsOn);
	const end = parseChallengeISODate(endsOn);
	if (!start || !end || end < start) return [];

	const days = [];
	const cursor = new Date(start);
	while (cursor <= end) {
		days.push(toChallengeISODate(cursor));
		cursor.setDate(cursor.getDate() + 1);
	}
	return days;
}

export function formatChallengeDayLabel(iso) {
	const parsed = parseChallengeISODate(iso);
	if (!parsed) return iso;
	return parsed.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function isDayTrackingVisible(dayTracking) {
	return Boolean(dayTracking?.available);
}

export function canEditDayTracking(dayTracking) {
	return Boolean(dayTracking?.available && dayTracking?.can_track);
}

export function isDayChecked(dayTracking, isoDate) {
	return Boolean(dayTracking?.checked_dates?.includes(isoDate));
}

export function isFutureChallengeDay(isoDate, todayIso) {
	if (!isoDate || !todayIso) return false;
	return isoDate > todayIso;
}

export function isMultidayChallenge(challenge) {
	if (!challenge?.starts_on || !challenge?.ends_on) return false;
	return challenge.starts_on < challenge.ends_on;
}

export function computeCompletionReadiness(dayTracking, challenge) {
	if (!dayTracking?.available || !isMultidayChallenge(challenge)) {
		return dayTracking;
	}

	const todayIso = todayChallengeISODate(dayTracking);
	const periodFinished = todayIso > challenge.ends_on;
	const calendarDays = challengeCalendarDays(challenge.starts_on, challenge.ends_on);
	const checkedSet = new Set(dayTracking.checked_dates || []);
	const missingDates = calendarDays.filter((isoDate) => !checkedSet.has(isoDate));
	const readyForCompletion = missingDates.length === 0;

	let blockedMessage = null;
	if (!readyForCompletion) {
		if (missingDates.length === 1) {
			blockedMessage = 'Mark 1 remaining day in daily progress to finish.';
		} else if (missingDates.length > 1) {
			blockedMessage = `Mark ${missingDates.length} remaining days in daily progress to finish.`;
		} else {
			blockedMessage = 'Mark each day in daily progress before you can finish the challenge.';
		}
	}

	return {
		...dayTracking,
		applies: true,
		period_finished: periodFinished,
		ready_for_completion: readyForCompletion,
		missing_dates: missingDates,
		blocked_message: blockedMessage
	};
}

export function deriveCanComplete(challenge) {
	if (!challenge?.can_fail) return false;
	if (!isMultidayChallenge(challenge)) return true;

	const dayTracking = challenge.day_tracking;
	if (dayTracking?.available) {
		return Boolean(dayTracking.ready_for_completion);
	}

	return Boolean(challenge.can_complete);
}

export function buildOptimisticDayTracking(dayTracking, challenge, isoDate, checked) {
	if (!dayTracking?.available) return dayTracking;

	const checkedSet = new Set(dayTracking.checked_dates || []);
	if (checked) {
		checkedSet.add(isoDate);
	} else {
		checkedSet.delete(isoDate);
	}

	const checkedDates = Array.from(checkedSet).sort();
	const nextTracking = {
		...dayTracking,
		checked_dates: checkedDates,
		checked_count: checkedDates.length
	};

	return computeCompletionReadiness(nextTracking, challenge);
}

export function mergeChallengeDayTracking(challenges, challenge, dayTracking) {
	return challenges.map((item) => {
		if (item.kind !== challenge.kind || item.id !== challenge.id) return item;

		const nextTracking = dayTracking?.available
			? computeCompletionReadiness(dayTracking, item)
			: dayTracking;
		const updated = { ...item, day_tracking: nextTracking };

		return {
			...updated,
			can_complete: deriveCanComplete(updated)
		};
	});
}

export function dayTrackingProgressPercent(dayTracking) {
	if (!dayTracking?.available || !dayTracking.total_days) return 0;
	return Math.min(100, Math.round((dayTracking.checked_count / dayTracking.total_days) * 100));
}
